import { createSlice } from '@reduxjs/toolkit';
import { ERROR_CONSTS, STATUS } from '../../../util/constants';

const FETCH_ASSIGNABLE_APPLICATIONS = 'FETCH_ASSIGNABLE_APPLICATIONS';
const MANUAL_BATCH_ASSIGN_APPLICATIONS = 'MANUAL_BATCH_ASSIGN_APPLICATIONS';
const MANUAL_BATCH_UNASSIGN_APPLICATIONS = 'MANUAL_BATCH_UNASSIGN_APPLICATIONS';
const BULK_PRIORITIZE_APPLICATIONS = 'BULK_PRIORITIZE_APPLICATIONS';
const FETCH_ASSIGNED_APPLICATIONS = 'FETCH_ASSIGNED_APPLICATIONS';
const FETCH_REVIEWER_BY_ID = 'FETCH_REVIEWER_BY_ID';
const ASSIGN_APPLICATION = 'ASSIGN_APPLICATION';
const UNASSIGN_APPLICATION = 'UNASSIGN_APPLICATION';
const REASSIGN_APPLICATION = 'REASSIGN_APPLICATION';

// ---------------------------------------------------------------------------
// Shared helpers (moved from ManageApplications/reducers.js)
// ---------------------------------------------------------------------------
const initialState = {
  status: '',
  alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
  data: { applications: [], columns: [], filterOptions: [] },
};

function transformApplications(data) {
  data = convertJsonStringsToOptions(data);

  const applications = data.applications;
  if (!Array.isArray(applications)) {
    return data;
  }

  data.applications.forEach?.((application) => {
    Object.assign(application, convertJsonToMap(application.dataAsJsonMap));
    application.id = application.applicantEmployeeId + ' - ' + application.term;
  });
}

function convertJsonStringsToOptions(data, oldFilterOptionsCustom) {
  if (typeof data.filterOptionsAsJsonArr === 'string') {
    const filterOptions = JSON.parse(data.filterOptionsAsJsonArr) ?? [];
    data.filterOptionsOriginal = filterOptions;
    data.filterOptionsCustom = oldFilterOptionsCustom ?? [];

    data.filterOptions = [...data.filterOptionsOriginal, ...data.filterOptionsCustom];
  }
  return data;
}

function convertJsonToMap(str) {
  try {
    return JSON.parse(str);
  } catch (error) {
    return {};
  }
}

function getSafeCustomFilters(str) {
  if (!str) {
    return;
  }

  try {
    return JSON.parse(str);
  } catch (exception) {
    console.error('Error while parsing custom filters from str', str, exception);
  }

  return [];
}

// ---------------------------------------------------------------------------
// manageApplicationsMultiReducer slice
// ---------------------------------------------------------------------------
const multiInitialState = {
  assignableApplications: {
    ...initialState,
    type: FETCH_ASSIGNABLE_APPLICATIONS,
  },
  manualBatchAssignApplications: {
    ...initialState,
    data: '',
    type: MANUAL_BATCH_ASSIGN_APPLICATIONS,
  },
  manualBatchUnassignApplications: {
    ...initialState,
    data: '',
    type: MANUAL_BATCH_UNASSIGN_APPLICATIONS,
  },
  bulkPrioritizeApplications: {
    ...initialState,
    data: '',
    type: BULK_PRIORITIZE_APPLICATIONS,
  },
  assignedApplications: {
    ...initialState,
    type: FETCH_ASSIGNED_APPLICATIONS,
  },
};

const manageApplicationsMultiSlice = createSlice({
  name: 'manageApplicationsMultiReducer',
  initialState: multiInitialState,
  reducers: {
    fetchAssignableApplications(state, action) {
      let finalData = state.assignableApplications?.data ?? initialState.data;
      const uidForApiCall =
        (action.payload?.status === STATUS.LOADING
          ? action.payload.uidForApiCall
          : state.assignableApplications?.uidForApiCall) ??
        state.assignableApplications?.uidForApiCall;

      if (action.payload?.status === STATUS.CLEAR) {
        finalData = initialState.data;
      }

      if (action.payload?.status === STATUS.SUCCESS) {
        if (
          action.payload.uidForApiCall ===
          state.assignableApplications?.uidForApiCall
        ) {
          finalData = action.payload.data;
          transformApplications(finalData);

          finalData = {
            applications: [
              ...finalData.applications,
              ...state.assignableApplications.data.applications,
            ],
            lastKey: finalData.lastKey,
          };
        }
      }

      return {
        ...state,
        assignableApplications: {
          status: action.payload.status ?? initialState.status,
          uidForApiCall,
          alert: action.payload.alert ?? initialState.alert,
          data: finalData,
          type: FETCH_ASSIGNABLE_APPLICATIONS,
        },
      };
    },
    
    refetchAssignableApplications(state) { return { ...state, refetchAssignable: new Date().toISOString() }; },
    refetchAssignedApplications(state) { return { ...state, refetchAssigned: new Date().toISOString() }; },

    manualBatchAssignApplications(state, action) {
      return {
        ...state,
        manualBatchAssignApplications: {
          status: action.payload.status ?? initialState.status,
          alert: action.payload.alert ?? initialState.alert,
          data: action.payload.data,
          type: MANUAL_BATCH_ASSIGN_APPLICATIONS,
        },
        manualBatchUnassignApplications: {
          ...initialState,
          data: '',
          type: MANUAL_BATCH_UNASSIGN_APPLICATIONS,
        },
        bulkPrioritizeApplications: {
          ...initialState,
          data: '',
          type: BULK_PRIORITIZE_APPLICATIONS,
        },
      };
    },

    manualBatchUnassignApplications(state, action) {
      return {
        ...state,
        manualBatchUnassignApplications: {
          status: action.payload.status ?? initialState.status,
          alert: action.payload.alert ?? initialState.alert,
          data: action.payload.data,
          type: MANUAL_BATCH_UNASSIGN_APPLICATIONS,
        },
        manualBatchAssignApplications: {
          ...initialState,
          data: '',
          type: MANUAL_BATCH_ASSIGN_APPLICATIONS,
        },
        bulkPrioritizeApplications: {
          ...initialState,
          data: '',
          type: BULK_PRIORITIZE_APPLICATIONS,
        },
      };
    },

    bulkPrioritizeApplications(state, action) {
      return {
        ...state,
        bulkPrioritizeApplications: {
          status: action.payload.status ?? initialState.status,
          alert: action.payload.alert ?? initialState.alert,
          data: action.payload.data,
          type: BULK_PRIORITIZE_APPLICATIONS,
        },
        manualBatchAssignApplications: {
          ...initialState,
          data: '',
          type: MANUAL_BATCH_ASSIGN_APPLICATIONS,
        },
        manualBatchUnassignApplications: {
          ...initialState,
          data: '',
          type: MANUAL_BATCH_UNASSIGN_APPLICATIONS,
        },
      };
    },

    fetchAssignedApplications(state, action) {
      let finalData = action.payload.data ?? initialState.data;
      transformApplications(finalData);

      return {
        ...state,
        assignedApplications: {
          status: action.payload.status ?? initialState.status,
          alert: action.payload.alert ?? initialState.alert,
          data: finalData,
          type: FETCH_ASSIGNED_APPLICATIONS,
        },
      };
    },

    subscribeCommitteeChangesMulti(state, action) {
      return {
        ...state,
        committeeSubscription: {
          data: action.payload,
        },
      };
    },

    setSelectedReviewer(state, action) {
      return {
        ...state,
        selectedReviewer: action.payload,
      };
    },
  },
});

export const {
  fetchAssignableApplications,
  refetchAssignableApplications,
  manualBatchAssignApplications: manualBatchAssignApplicationsAction,
  manualBatchUnassignApplications: manualBatchUnassignApplicationsAction,
  bulkPrioritizeApplications: bulkPrioritizeApplicationsAction,
  fetchAssignedApplications,
  refetchAssignedApplications,
  subscribeCommitteeChangesMulti,
  setSelectedReviewer,
} = manageApplicationsMultiSlice.actions;

export const manageApplicationsMultiReducer = manageApplicationsMultiSlice.reducer;


// ---------------------------------------------------------------------------
// manageApplicationsAssignedReducer slice
// ---------------------------------------------------------------------------
const assignedInitialState = {
  assignedCounts: {
    status: '',
    alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ERROR_OBJECT,
    data: [],
    type: FETCH_REVIEWER_BY_ID,
  },
};

const manageApplicationsAssignedSlice = createSlice({
  name: 'manageApplicationsAssignedReducer',
  initialState: assignedInitialState,
  reducers: {
    fetchReviewerById(state, action) {
      return {
        ...state,
        assignedCounts: {
          status: action.payload.status ?? '',
          alert: action.payload.alert ?? ERROR_CONSTS.UI.GENERIC.EMPTY_ERROR_OBJECT,
          data: action.payload.data,
          type: FETCH_REVIEWER_BY_ID,
        },
      };
    },

    fetchCommitteeDetails(state, action) {
      return {
        ...state,
        committeeDetails: action.payload.data,
      };
    },

    subscribeReviewerChanges(state, action) {
      // Store the subscription payload directly so the AssignDrawer can merge it
      // with RTK Query data. The payload contains { committeeId, pending, submitted, total }.
      const payload = action.payload;
      if (!payload) return state;

      const existing = state.assignedCounts?.data?.committeeDetails ?? [];
      const updatedDetails = [...existing];
      const idx = updatedDetails.findIndex(c => c.committeeId === payload.committeeId);
      if (idx >= 0) {
        updatedDetails[idx] = { ...updatedDetails[idx], ...payload };
      } else {
        updatedDetails.push(payload);
      }

      return {
        ...state,
        assignedCounts: {
          ...state.assignedCounts,
          data: { ...state.assignedCounts?.data, committeeDetails: updatedDetails },
        },
      };
    },

    subscribeCommitteeChangesAssigned(state, action) {
      return {
        ...state,
        committeeDetails: action.payload,
      };
    },
  },
});

export const {
  fetchReviewerById,
  fetchCommitteeDetails,
  subscribeReviewerChanges,
  subscribeCommitteeChangesAssigned,
} = manageApplicationsAssignedSlice.actions;

export const manageApplicationsAssignedReducer = manageApplicationsAssignedSlice.reducer;

// ---------------------------------------------------------------------------
// assignApplicationReducer slice
// ---------------------------------------------------------------------------
const assignAppInitialState = {
  assignApplication: {
    status: '',
    alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
    type: ASSIGN_APPLICATION,
  },
};

const assignApplicationSlice = createSlice({
  name: 'assignApplicationReducer',
  initialState: assignAppInitialState,
  reducers: {
    assignApplication(state, action) {
      return {
        ...state,
        assignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: ASSIGN_APPLICATION,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ASSIGN_APPLICATION, (state, action) => {
      return {
        ...state,
        assignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: ASSIGN_APPLICATION,
        },
      };
    });
  },
});

export const { assignApplication } = assignApplicationSlice.actions;
export const assignApplicationReducer = assignApplicationSlice.reducer;

// ---------------------------------------------------------------------------
// unassignApplicationReducer slice
// ---------------------------------------------------------------------------
const unassignAppInitialState = {
  unassignApplication: {
    status: '',
    alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
    type: UNASSIGN_APPLICATION,
  },
};

const unassignApplicationSlice = createSlice({
  name: 'unassignApplicationReducer',
  initialState: unassignAppInitialState,
  reducers: {
    unassignApplication(state, action) {
      return {
        ...state,
        unassignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: UNASSIGN_APPLICATION,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(UNASSIGN_APPLICATION, (state, action) => {
      return {
        ...state,
        unassignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: UNASSIGN_APPLICATION,
        },
      };
    });
  },
});

export const { unassignApplication } = unassignApplicationSlice.actions;
export const unassignApplicationReducer = unassignApplicationSlice.reducer;

// ---------------------------------------------------------------------------
// reassignApplicationReducer slice
// ---------------------------------------------------------------------------
const reassignAppInitialState = {
  reassignApplication: {
    status: '',
    alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
    type: REASSIGN_APPLICATION,
  },
};

const reassignApplicationSlice = createSlice({
  name: 'reassignApplicationReducer',
  initialState: reassignAppInitialState,
  reducers: {
    reassignApplication(state, action) {
      return {
        ...state,
        reassignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: REASSIGN_APPLICATION,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REASSIGN_APPLICATION, (state, action) => {
      return {
        ...state,
        reassignApplication: {
          status: action.payload.status,
          alert: action.payload.alert,
          type: REASSIGN_APPLICATION,
        },
      };
    });
  },
});

export const { reassignApplication } = reassignApplicationSlice.actions;
export const reassignApplicationReducer = reassignApplicationSlice.reducer;
